import type { DashboardContract, Locale } from "@chams-pay/contracts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@chams-pay/design-system/components/chart";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useId, useState } from "react";
import {
  Bar,
  BarChart,
  type BarShapeProps,
  CartesianGrid,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  Rectangle,
  XAxis,
  YAxis,
} from "recharts";
import { completeDashboardActivity } from "../core/format.js";
import { Amount, Percentage, UnavailableFigure } from "./figures.js";

function dateLabel(value: string, locale: Locale, monthOnly = false): string {
  const date = /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value.slice(0, 10);
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn", {
    month: "short",
    ...(monthOnly ? {} : { day: "numeric" }),
  }).format(new Date(`${date}T12:00:00Z`));
}

function activityPeriodLabel(
  value: string,
  locale: Locale,
  granularity: DashboardContract["activity"]["granularity"],
): string {
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn", {
    year: "numeric",
    month: "long",
    ...(granularity === "day" ? { day: "numeric" } : {}),
  }).format(new Date(`${value.slice(0, 10)}T12:00:00Z`));
}

function activityTickLabel(
  value: string,
  locale: Locale,
  granularity: DashboardContract["activity"]["granularity"],
): string {
  if (granularity === "month") return dateLabel(value, locale, true);
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn", {
    day: "numeric",
  }).format(new Date(`${value.slice(0, 10)}T12:00:00Z`));
}

function useMobileChartTooltip(compact: boolean) {
  const chartId = `dashboard-chart-${useId().replace(/[^a-z0-9]/gi, "")}`;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!compact) return;
    const close = () => setOpen(false);
    const dismissOutside = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(`[data-dashboard-chart="${chartId}"]`))
        return;
      close();
    };
    const closeForOtherChart = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== chartId) close();
    };
    document.addEventListener("pointerdown", dismissOutside, true);
    window.addEventListener("scroll", close, true);
    window.addEventListener("dashboard-chart-tooltip-open", closeForOtherChart);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside, true);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("dashboard-chart-tooltip-open", closeForOtherChart);
    };
  }, [chartId, compact]);

  const select = (activeIndex: number | string | null | undefined) => {
    if (!compact) return;
    const nextOpen = activeIndex != null;
    setOpen(nextOpen);
    if (nextOpen)
      window.dispatchEvent(new CustomEvent("dashboard-chart-tooltip-open", { detail: chartId }));
  };

  return { chartId, open, select };
}

export function AcademicYearSelector({
  collection,
  locale,
  onChange,
  compact = false,
}: {
  readonly collection: DashboardContract["academicCollection"];
  readonly locale: Locale;
  readonly onChange: (academicYear: string) => void;
  readonly compact?: boolean;
}) {
  const years = collection.availableAcademicYears;
  const index = years.indexOf(collection.academicYear);
  const previous = index > 0 ? years[index - 1] : undefined;
  const next = index >= 0 && index < years.length - 1 ? years[index + 1] : undefined;
  const previousLabel = locale === "ar" ? "الموسم الدراسي السابق" : "Année scolaire précédente";
  const nextLabel = locale === "ar" ? "الموسم الدراسي التالي" : "Année scolaire suivante";

  return (
    <div
      dir="ltr"
      className={`flex items-center rounded-full border border-input bg-card shadow-e1 ${
        compact ? "h-9 w-full" : "h-8 w-fit shrink-0"
      }`}
    >
      <button
        type="button"
        aria-label={previousLabel}
        disabled={!previous}
        onClick={() => previous && onChange(previous)}
        className="chams-focusable grid size-8 place-items-center rounded-full text-muted-foreground disabled:cursor-not-allowed disabled:opacity-30"
      >
        <ChevronLeft aria-hidden="true" className="size-4" />
      </button>
      <strong
        className={`chams-figure min-w-[92px] px-2 text-center text-[12px] ${
          compact ? "flex-1" : ""
        }`}
      >
        {collection.academicYear}
      </strong>
      <button
        type="button"
        aria-label={nextLabel}
        disabled={!next}
        onClick={() => next && onChange(next)}
        className="chams-focusable grid size-8 place-items-center rounded-full text-muted-foreground disabled:cursor-not-allowed disabled:opacity-30"
      >
        <ChevronRight aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}

export function CollectionGauge({
  percentage,
  label,
  compact = false,
  fillHeight = false,
  responsive = false,
}: {
  readonly percentage: string | null;
  readonly label: string;
  readonly compact?: boolean;
  readonly fillHeight?: boolean;
  readonly responsive?: boolean;
}) {
  const value = percentage === null ? 0 : Math.max(0, Math.min(100, Number(percentage)));
  const size = responsive
    ? "w-full aspect-square"
    : fillHeight
      ? "h-full aspect-square"
      : compact
        ? "size-[88px]"
        : "size-[104px]";
  return (
    <div className={`relative shrink-0 ${size}`} role="img" aria-label={label}>
      <ChartContainer
        config={{ value: { color: "rgb(42 138 148)" } }}
        className="absolute inset-0 aspect-square [-webkit-tap-highlight-color:transparent]"
        initialDimension={{
          width: responsive ? 128 : fillHeight ? 160 : compact ? 88 : 104,
          height: responsive ? 128 : fillHeight ? 160 : compact ? 88 : 104,
        }}
      >
        <RadialBarChart
          data={[{ value, fill: "var(--color-value)" }]}
          accessibilityLayer={false}
          startAngle={90}
          endAngle={-270}
          innerRadius="72%"
          outerRadius="100%"
        >
          <PolarAngleAxis type="number" domain={[0, 100]} dataKey="value" tick={false} />
          <RadialBar dataKey="value" background cornerRadius={8} />
        </RadialBarChart>
      </ChartContainer>
      <div className="pointer-events-none absolute inset-[13%] grid place-items-center rounded-full bg-card">
        {percentage === null ? (
          <UnavailableFigure
            className={
              responsive
                ? "text-[clamp(1.25rem,9cqi,1.75rem)] font-extrabold"
                : fillHeight
                  ? "text-2xl font-extrabold"
                  : compact
                    ? "text-lg font-extrabold"
                    : "text-xl font-extrabold"
            }
          />
        ) : (
          <Percentage
            value={value}
            className={
              responsive
                ? "text-[clamp(1.25rem,9cqi,1.75rem)] font-extrabold"
                : fillHeight
                  ? "text-[28px] font-extrabold"
                  : compact
                    ? "text-[21px] font-extrabold"
                    : "text-2xl font-extrabold"
            }
            unitClassName={
              responsive
                ? "text-[clamp(0.625rem,4cqi,0.8125rem)] text-muted-foreground"
                : fillHeight
                  ? "text-[13px] text-muted-foreground"
                  : compact
                    ? "text-[11px] text-muted-foreground"
                    : "text-[13px] text-muted-foreground"
            }
          />
        )}
      </div>
    </div>
  );
}

export function ActivityCollectionsChart({
  activity,
  period,
  locale,
  label,
  compact = false,
}: {
  readonly activity: DashboardContract["activity"];
  readonly period: DashboardContract["period"];
  readonly locale: Locale;
  readonly label: string;
  readonly compact?: boolean;
}) {
  const mobileTooltip = useMobileChartTooltip(compact);
  const config = {
    cashMad: { label: locale === "ar" ? "نقداً" : "Espèces", color: "rgb(42 138 148)" },
    chequeMad: { label: locale === "ar" ? "شيك" : "Chèque", color: "rgb(90 172 180)" },
    transferMad: { label: locale === "ar" ? "تحويل" : "Virement", color: "rgb(143 201 206)" },
  } satisfies ChartConfig;
  const completedActivity = completeDashboardActivity(activity, period);
  const data = completedActivity.points.map((point) => ({
    period: point.period,
    cashMad: Number(point.cashMad),
    cashValue: point.cashMad,
    chequeMad: Number(point.chequeMad),
    chequeValue: point.chequeMad,
    transferMad: Number(point.transferMad),
    transferValue: point.transferMad,
  }));
  return (
    <ChartContainer
      config={config}
      className={`${compact ? "h-44" : "h-52"} w-full aspect-auto [-webkit-tap-highlight-color:transparent]`}
      role="img"
      aria-label={label}
      data-dashboard-chart={mobileTooltip.chartId}
    >
      <BarChart
        data={data}
        accessibilityLayer={false}
        margin={{ top: 8, right: 4, bottom: 0, left: 0 }}
        onClick={(state) => mobileTooltip.select(state?.activeTooltipIndex)}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="period"
          tickLine={false}
          axisLine={false}
          interval={0}
          tick={{ fontSize: compact ? 9 : 10 }}
          tickFormatter={(value: string) =>
            activityTickLabel(value, locale, completedActivity.granularity)
          }
        />
        <YAxis hide />
        <ChartTooltip
          {...(compact ? { active: mobileTooltip.open } : {})}
          trigger={compact ? "click" : "hover"}
          shared
          cursor={{ fill: "rgb(42 138 148 / 0.08)" }}
          content={({ active, payload }) => {
            const point = payload?.[0]?.payload as (typeof data)[number] | undefined;
            if (!active || !point) return null;
            const items = [
              {
                key: "cash",
                label: config.cashMad.label,
                value: point.cashValue,
                color: config.cashMad.color,
              },
              {
                key: "cheque",
                label: config.chequeMad.label,
                value: point.chequeValue,
                color: config.chequeMad.color,
              },
              {
                key: "transfer",
                label: config.transferMad.label,
                value: point.transferValue,
                color: config.transferMad.color,
              },
            ];
            return (
              <div
                dir={locale === "ar" ? "rtl" : "ltr"}
                className="grid min-w-40 gap-2 rounded-md border border-border/50 bg-card px-3 py-2 text-start text-xs text-foreground shadow-xl"
              >
                <strong className="font-heading text-sm">
                  {activityPeriodLabel(point.period, locale, completedActivity.granularity)}
                </strong>
                <div className="grid justify-items-start gap-1.5">
                  {items.map((item) => (
                    <span key={item.key} className="flex items-center gap-2 text-muted-foreground">
                      <span
                        aria-hidden="true"
                        className="size-2.5 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: item.color }}
                      />
                      <span>{item.label}</span>
                      <Amount
                        value={item.value}
                        locale={locale}
                        className="font-semibold text-foreground"
                      />
                    </span>
                  ))}
                </div>
              </div>
            );
          }}
        />
        <Bar
          dataKey="cashMad"
          stackId="receipts"
          fill="var(--color-cashMad)"
          shape={(props: BarShapeProps) => (
            <Rectangle
              x={props.x}
              y={props.y}
              width={props.width}
              height={props.height}
              fill={props.fill}
              radius={
                data[props.index]?.chequeMad === 0 && data[props.index]?.transferMad === 0
                  ? [4, 4, 0, 0]
                  : 0
              }
            />
          )}
        />
        <Bar
          dataKey="chequeMad"
          stackId="receipts"
          fill="var(--color-chequeMad)"
          shape={(props: BarShapeProps) => (
            <Rectangle
              x={props.x}
              y={props.y}
              width={props.width}
              height={props.height}
              fill={props.fill}
              radius={
                data[props.index]?.transferMad === 0 && data[props.index]?.chequeMad !== 0
                  ? [4, 4, 0, 0]
                  : 0
              }
            />
          )}
        />
        <Bar
          dataKey="transferMad"
          stackId="receipts"
          fill="var(--color-transferMad)"
          shape={(props: BarShapeProps) => (
            <Rectangle
              x={props.x}
              y={props.y}
              width={props.width}
              height={props.height}
              fill={props.fill}
              radius={data[props.index]?.transferMad !== 0 ? [4, 4, 0, 0] : 0}
            />
          )}
        />
      </BarChart>
    </ChartContainer>
  );
}

export function AcademicCollectionChart({
  collection,
  locale,
  label,
  compact = false,
}: {
  readonly collection: DashboardContract["academicCollection"];
  readonly locale: Locale;
  readonly label: string;
  readonly compact?: boolean;
}) {
  const mobileTooltip = useMobileChartTooltip(compact);
  const config = {
    percentage: {
      label: locale === "ar" ? "نسبة التحصيل" : "Taux de recouvrement",
      color: "#2a8a94",
    },
  } satisfies ChartConfig;
  const data = collection.points.map((point) => ({
    month: point.month,
    percentage: point.percentage === null ? 0 : Number(point.percentage),
    percentageLabel: point.percentage,
    netObligationsMad: point.netObligationsMad,
    settledAllocationsMad: point.settledAllocationsMad,
  }));
  return (
    <ChartContainer
      config={config}
      className={`${compact ? "h-44" : "h-52"} w-full aspect-auto [-webkit-tap-highlight-color:transparent]`}
      role="img"
      aria-label={label}
      data-dashboard-chart={mobileTooltip.chartId}
      dir="ltr"
    >
      <BarChart
        data={data}
        accessibilityLayer={false}
        margin={{ top: 8, right: 4, bottom: 0, left: 0 }}
        onClick={(state) => mobileTooltip.select(state?.activeTooltipIndex)}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          interval={compact ? 1 : 0}
          tickFormatter={(value: string) => dateLabel(value, locale, true)}
        />
        <YAxis
          width={compact ? 32 : 38}
          domain={[0, 100]}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value: number) => `${value}%`}
        />
        <ChartTooltip
          {...(compact ? { active: mobileTooltip.open } : {})}
          trigger={compact ? "click" : "hover"}
          shared={false}
          cursor={{ fill: "rgb(42 138 148 / 0.08)" }}
          content={({ active, payload }) => {
            const point = payload?.[0]?.payload as (typeof data)[number] | undefined;
            if (!active || !point) return null;
            return (
              <div
                dir={locale === "ar" ? "rtl" : "ltr"}
                className="grid min-w-40 gap-2 rounded-md border border-border/50 bg-card px-3 py-2 text-xs shadow-xl"
              >
                <strong className="chams-figure text-sm">
                  {compact && `${dateLabel(point.month, locale, true)} · `}
                  {point.percentageLabel === null ? "—" : `${point.percentageLabel}%`}
                </strong>
                <div className="grid gap-1 text-muted-foreground">
                  <span className="flex items-center justify-between gap-4">
                    {locale === "ar" ? "المتوقع" : "Attendu"}
                    <Amount
                      value={point.netObligationsMad}
                      locale={locale}
                      className="font-semibold text-foreground"
                    />
                  </span>
                  <span className="flex items-center justify-between gap-4">
                    {locale === "ar" ? "المحصّل" : "Encaissé"}
                    <Amount
                      value={point.settledAllocationsMad}
                      locale={locale}
                      className="font-semibold text-foreground"
                    />
                  </span>
                </div>
              </div>
            );
          }}
        />
        <Bar dataKey="percentage" fill="var(--color-percentage)" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ChartContainer>
  );
}
